import React from "react";
import {
    Box,
    Button,
    Stack,
    SvgIcon,
} from "@mui/material";
import { useSelection } from "../../hooks/use-selection";
import { CustomTable } from "../../components/custom-table";
import { CustomSearch } from "../../components/custom-search";
import { PageHeader } from "../../components/page-header";
import { externalVendorOrdersHeadCells } from "../../seed/table-headers";
import {
    CREATE,
    UPDATE,
    vendorOrderStatus,
    getColumnName,
    customTabsStyle,
    getRegionId,
    getDistrictId,
    getWardId,
    ROWSPERPAGE,
} from "../../utils/constant";
import { getRequest, postRequest } from "../../services/api-service";
import {
    createExternalOrderUrl,
    deleteExternalOrderUrl,
    getAllDistrictsUrl,
    getAllExternalOrdersByPaginationUrl,
    getAllRegionsUrl,
    getAllWardsUrl,
    updateExternalOrderUrl,
} from "../../seed/url";
import { useDispatch, useSelector } from "react-redux";
import PlusIcon from "@heroicons/react/24/outline/PlusIcon";
import FolderArrowDownIcon from "@heroicons/react/24/outline/FolderArrowDownIcon";
import ChevronDownIcon from "@heroicons/react/24/outline/ChevronDownIcon";
import DocumentDuplicateIcon from "@heroicons/react/24/outline/DocumentDuplicateIcon";
import { FormDialog } from "../../components/form-dialog";
import { vendorOrderFormFields } from "../../seed/form-fields";
import DataFeedUploadDialog from "./DataFeedUploadDialog";
import { read, utils } from "xlsx";
import { usePopover } from "../../hooks/use-popover";
import FilterOrderPopper from "./FilterOrderPopper";
import PencilIcon from "@heroicons/react/24/outline/PencilIcon";
import { ConfirmationDialog } from "../../components/confirmation-dialog";
import TrashIcon from "@heroicons/react/24/outline/TrashIcon";
import { CustomAlert } from "../../components/custom-alert";
import { handleExport } from "./ImportedOrders";
import { CustomTabs } from "../../components/CustomTabs";
import { useAuth } from "../../hooks/use-auth";
import { useOrderTimeRange } from "../../hooks/use-order-time-range";
import VendorViewOrder from "./VendorViewOrder";
import EyeIcon from "@heroicons/react/24/outline/EyeIcon";

const useProductsOrdersIds = (externalOrders) => {
    return React.useMemo(() => {
        return externalOrders.map((customer) => customer.id);
    }, [externalOrders]);
};

function VendorOrders() {
    const auth = useAuth();
    const dispatch = useDispatch();
    const formInfo = useSelector((state) => state.FormInformationReducer);
    const popOver = usePopover();
    const [action, setAction] = React.useState(CREATE);
    const [currentTab, setCurrentTab] = React.useState("");
    const orderSideNav = useSelector((state) => state.ViewPaymentSideNavReducer);
    const [rowsPerPage, setRowsPerPage] = React.useState(ROWSPERPAGE);
    const [regions, setRegions] = React.useState([]);
    const [districts, setDistricts] = React.useState([]);
    const [wards, setWards] = React.useState([]);
    let newVendorOrderFormFields = vendorOrderFormFields;
    newVendorOrderFormFields[7].searchBody = {
        ...newVendorOrderFormFields[7].searchBody,
        vendor_id: auth?.user?.id
    };
    const [formFields, setFormFields] = React.useState(newVendorOrderFormFields);
    const [productsOrders, setProductsOrders] = React.useState({
        page: 1,
        total_results: 0,
        total_pages: 0,
        results: [],
    });
    const [selectedData, setSelectedData] = React.useState({});
    const [filterItemsData, setFilterItemsData] = React.useState({
        location: "",
        order_status: "",
        payment_status: "",
    });
    const [searchTerm, setSearchTerm] = React.useState("");
    const [isLoading, setIsLoading] = React.useState(true);
    const [exportExcel, setExportExcel] = React.useState(false);
    const [isDeleting, setIsDeleting] = React.useState(false);
    const productsIds = useProductsOrdersIds(productsOrders.results);
    const productsOrdersSelection = useSelection(productsIds);
    const [openCreateDialog, setOpenCreateDialog] = React.useState(false);
    const [openDataFeedUploadOrdersDialog, setOpenDataFeedUploadOrdersDialog] =
        React.useState(false);
    const [openViewDialog, setOpenViewDialog] = React.useState(false);
    const [openDeleteDialog, setOpenDeleteDialog] = React.useState(false);
    const [openAlert, setOpenAlert] = React.useState(false);
    const [severity, setSeverity] = React.useState("");
    const [severityMessage, setSeverityMessage] = React.useState("");
    const [ordersData, setOrdersData] = React.useState({
        rows: [],
        originalColumns: [],
        mappedColumns: [],
        file: null,
        vendorID: auth?.user?.id,
    });
    const values = [
        {
            id: action === UPDATE ? selectedData.id : 0,
            full_name: action === UPDATE ? selectedData.full_name : "",
            region: action === UPDATE ? getRegionId(regions, selectedData.region) : "",
            district: action === UPDATE ? getDistrictId(districts, selectedData.district) : "",
            ward: action === UPDATE ? getWardId(wards, selectedData.ward) : "",
            street: action === UPDATE ? selectedData.street : "",
            phone_number: action === UPDATE ? selectedData.phone_number : "",
            delivery_instruction:
                action === UPDATE ? selectedData.delivery_instruction : "",
            cart: action === UPDATE ? selectedData.cart : "",
            product_quantity:
                action === UPDATE ? selectedData?.product_quantity || 0 : 0,
            s_k_u: action === UPDATE ? selectedData.s_k_u : "",
            product_amount: action === UPDATE ? selectedData.product_amount : 0,
            shipping_cost: action === UPDATE ? selectedData.shipping_cost : auth?.user?.outbound_recharge_amount,
            return_amount: action === UPDATE ? selectedData.return_amount : auth?.user?.outbound_return_amount,
            product_vendor_id: action === UPDATE ? selectedData.product_vendor_id : auth?.user?.id,
        },
    ];
    const { timeRange, dateFrom, dateTo, handleDateChange, filterItems } = useOrderTimeRange();
    const [order, setOrder] = React.useState('desc');
    const [orderBy, setOrderBy] = React.useState('id');

    const handleRequestSort = (event, property) => {
        const isAsc = orderBy === property && order === 'asc';
        setOrder(isAsc ? 'desc' : 'asc');
        setOrderBy(property);
    };

    const getDataForExportExcel = () => {
        setExportExcel(true);
        postRequest(
            getAllExternalOrdersByPaginationUrl,
            {
                query: `${searchTerm}${filterItemsData.location === "" || searchTerm === ""
                    ? ""
                    : " " + filterItemsData.location
                    }${filterItemsData.order_status === ""
                        ? ""
                        : " " + filterItemsData.order_status
                    }${filterItemsData.payment_status === ""
                        ? ""
                        : " " + filterItemsData.payment_status
                    }`,
                status: currentTab,
                vendor_id: auth?.user?.id,
                from: timeRange.from,
                to: timeRange.to,
                sort: orderBy + " " + order,
                limit: productsOrders.total_results,
                page: 1,
            },
            (data) => {
                handleExport(data?.results);
                setExportExcel(false);
            },
            (error) => {
                setExportExcel(false);
            }
        );
    };

    const fetcher = React.useCallback(
        (page) => {
            setIsLoading(true);
            postRequest(
                getAllExternalOrdersByPaginationUrl,
                {
                    query: `${searchTerm}${filterItemsData.location === "" || searchTerm === ""
                        ? ""
                        : " " + filterItemsData.location
                        }${filterItemsData.order_status === ""
                            ? ""
                            : " " + filterItemsData.order_status
                        }${filterItemsData.payment_status === ""
                            ? ""
                            : " " + filterItemsData.payment_status
                        }`,
                    status: currentTab,
                    vendor_id: auth?.user?.id,
                    from: timeRange.from,
                    to: timeRange.to,
                    sort: orderBy + " " + order,
                    limit: rowsPerPage,
                    page: page,
                },
                (data) => {
                    setProductsOrders(data);
                    setIsLoading(false);
                },
                (error) => {
                    setProductsOrders({
                        page: 1,
                        total_results: 0,
                        total_pages: 0,
                        results: [],
                    });
                    setIsLoading(false);
                }
            );
        },
        [rowsPerPage, searchTerm, currentTab, filterItemsData, timeRange, orderBy, order, auth]
    );

    const handleSearch = (event) => {
        setSearchTerm(event.target.value);
    };

    const getAllDistricts = React.useCallback((regionId) => {
        setIsLoading(true)
        postRequest(
            getAllDistrictsUrl,
            {
                "id": regionId
            },
            (data) => {
                const newDistricts = data.map((category) => {
                    const newItem = {};
                    ["label", "value"].forEach((item) => {
                        if (item === "label") {
                            newItem[item] = category.district_name;
                        }
                        if (item === "value") {
                            newItem[item] = category.id;
                        }
                    });
                    return newItem;
                });
                let newFormFields = formFields;
                newFormFields[2].items = newDistricts;
                setFormFields(newFormFields);
                setDistricts(data)
                setIsLoading(false)
            },
            (error) => {
                let newFormFields = formFields;
                newFormFields[2].items = [];
                setFormFields(newFormFields);
                setIsLoading(false)
            },
        )
    }, [formFields])

    const getAllWards = React.useCallback((districtId) => {
        setIsLoading(true)
        postRequest(
            getAllWardsUrl,
            {
                "id": districtId
            },
            (data) => {
                const newDistricts = data.map((category) => {
                    const newItem = {};
                    ["label", "value"].forEach((item) => {
                        if (item === "label") {
                            newItem[item] = category.ward_name;
                        }
                        if (item === "value") {
                            newItem[item] = category.id;
                        }
                    });
                    return newItem;
                });
                let newFormFields = formFields;
                newFormFields[3].items = newDistricts;
                setFormFields(newFormFields);
                setWards(data)
                setIsLoading(false)
            },
            (error) => {
                let newFormFields = formFields;
                newFormFields[3].items = [];
                setFormFields(newFormFields);
                setIsLoading(false)
            },
        )
    }, [formFields])

    React.useEffect(() => {
        fetcher(1);
    }, [fetcher]);

    React.useEffect(() => {
        getRequest(
            getAllRegionsUrl,
            (data) => {
                setRegions(data);
            },
            (error) => {
                setIsLoading(false);
            }
        );
    }, [])

    React.useEffect(() => {
        if (formInfo.region) {
            getAllDistricts(formInfo?.region)
        }
    }, [formInfo, getAllDistricts])

    React.useEffect(() => {
        if (formInfo.district) {
            getAllWards(formInfo?.district)
        }
    }, [formInfo, getAllWards])

    React.useEffect(() => {
        if (regions.length > 0) {
            const newRegions = regions.map((region) => {
                const newItem = {};
                ["label", "value", "id"].forEach((item) => {
                    if (item === "label") {
                        newItem[item] = region.region_name;
                    }
                    if (item === "value") {
                        newItem[item] = region.id;
                    }
                });
                return newItem;
            });
            let newFormFields = formFields;
            newFormFields[1].items = newRegions;
            setFormFields(newFormFields);
        }
    }, [regions, formFields])

    const handlePageChange = React.useCallback(
        (event, value) => {
            fetcher(value + 1);
        },
        [fetcher]
    );

    const handleRowsPerPageChange = React.useCallback((event) => {
        setRowsPerPage(event.target.value);
    }, []);

    const handleClickOpenCreateDialog = () => {
        setOpenCreateDialog(true);
    };

    const handleCloseCreateDialog = () => {
        action === UPDATE ? fetcher(productsOrders.page) : fetcher(1);
        setOpenCreateDialog(false);
        setAction(CREATE);
    };

    const handleClickOpenDataFeedUploadOrdersDialog = () => {
        setOpenDataFeedUploadOrdersDialog(true);
    };

    const handleCloseDataFeedUloadOrdersDialog = () => {
        fetcher(1);
        setOpenDataFeedUploadOrdersDialog(false);
    };

    const handleClickOpenDeleteDialog = () => {
        setOpenDeleteDialog(false);
    };

    const handleCloseDeleteDialog = () => {
        setOpenDeleteDialog(false);
    };

    const handleClickAlert = () => {
        setOpenAlert(true);
    };

    const handleCloseAlert = (event, reason) => {
        if (reason === "clickaway") {
            return;
        }

        setOpenAlert(false);
    };

    const handleClickOpenViewDialog = () => {
        setOpenViewDialog(true)
    }

    const handleCloseViewDialog = () => {
        fetcher(productsOrders.page)
        setOpenViewDialog(false)
    }

    const onSelect = (data, openDialog) => {
        setSelectedData(data);
        getAllDistricts(getRegionId(regions, data.region))
        openDialog && handleClickOpenViewDialog()
    };

    const createMapping = (columns) => {
        const mapping = [
            {
                supportedColumn: "order_id",
                originalColumn: getColumnName(columns, "TRANSACTION NO"),
                columnRequired: false
            },
            {
                supportedColumn: "full_name",
                originalColumn: getColumnName(columns, "FULL NAME"),
                columnRequired: true
            },
            {
                supportedColumn: "region",
                originalColumn: getColumnName(columns, "REGION"),
                columnRequired: true
            },
            {
                supportedColumn: "district",
                originalColumn: getColumnName(columns, "DISTRICT"),
                columnRequired: true
            },
            {
                supportedColumn: "ward",
                originalColumn: getColumnName(columns, "WARD"),
                columnRequired: false
            },
            {
                supportedColumn: "street",
                originalColumn: getColumnName(columns, "STREET"),
                columnRequired: false
            },
            {
                supportedColumn: "phone_number",
                originalColumn: getColumnName(columns, "PHONE NUMBER(2557XXXXXXXX or 07XXXXXXXX)"),
                columnRequired: true
            },
            {
                supportedColumn: "delivery_instruction",
                originalColumn: getColumnName(columns, "DELIVERY INSRUCTION"),
                columnRequired: false
            },
            {
                supportedColumn: "cart",
                originalColumn: getColumnName(columns, "PRODUCT NAME"),
                columnRequired: true
            },
            {
                supportedColumn: "product_quantity",
                originalColumn: getColumnName(columns, "PRODUCT QUANTITY"),
                columnRequired: true
            },
            {
                supportedColumn: "s_k_u",
                originalColumn: getColumnName(columns, "SKU"),
                columnRequired: false
            },
            {
                supportedColumn: "product_amount",
                originalColumn: getColumnName(columns, "PRODUCT AMOUNT"),
                columnRequired: true
            },
            {
                supportedColumn: "shipping_cost",
                originalColumn: getColumnName(columns, "SHIPPING COST"),
                columnRequired: false
            },
            {
                supportedColumn: "return_amount",
                originalColumn: getColumnName(columns, "RETURN AMOUNT"),
                columnRequired: false
            },
        ];

        return mapping;
    };

    const processFile = (file) => {
        const reader = new FileReader();
        reader.onload = (e) => {
            const wb = read(e.target.result);
            const sheets = wb.SheetNames;

            if (sheets.length) {
                const rows = utils.sheet_to_json(wb.Sheets[sheets[0]]);
                if (rows.length === 0) {
                    return;
                }
                const columns = Object.keys(rows[0]);
                const filterEmptyRows = rows.filter((row) => {
                    const rowKeys = Object.keys(row);
                    return columns.every(
                        (column) =>
                            rowKeys.includes(column) &&
                            row[column] !== undefined &&
                            row[column] !== ""
                    );
                });
                const newRowsWithId = filterEmptyRows.map((row, index) => ({
                    ...row,
                    id: index + 1,
                }));
                const mappedColumns = createMapping(columns);
                const newOrdersData = {
                    ...ordersData,
                    rows: newRowsWithId,
                    originalColumns: columns,
                    mappedColumns: mappedColumns,
                    file: file,
                };
                setOrdersData(newOrdersData);
            }
        };
        reader.readAsArrayBuffer(file);
    };

    const handleDelete = async () => {
        setIsDeleting(true);
        postRequest(
            deleteExternalOrderUrl,
            {
                id: selectedData.id,
            },
            (data) => {
                fetcher(productsOrders.page);
                setSeverityMessage(data.message);
                setSeverity("success");
                handleClickAlert();
                setIsDeleting(false);
                handleCloseDeleteDialog();
            },
            (error) => {
                if (error?.response?.data?.message) {
                    setSeverityMessage(error.response.data.message[0]);
                    setSeverity("error");
                    handleClickAlert();
                }
                setIsDeleting(false);
            }
        );
    };

    const orderPopoverItems = [
        {
            id: "track_order",
            label: "Track Order",
            icon: (
                <SvgIcon fontSize="small" sx={{ color: "text.primary" }}>
                    <EyeIcon />
                </SvgIcon>
            ),
            onClick: () => {
                dispatch({
                    type: "TOOGLE_PAYMENT_SIDENAV",
                    payload: {
                        ...orderSideNav,
                        openViewOrderStatusTrackingSideNav: true,
                        orderStatusTrackingSideNavContent: selectedData,
                    },
                });
            },
        },
        {
            id: "edit",
            label: "Edit Order",
            icon: (
                <SvgIcon fontSize="small" sx={{ color: "text.primary" }}>
                    <PencilIcon />
                </SvgIcon>
            ),
            onClick: () => {
                setAction(UPDATE);
                if (selectedData.order_status === "RECEIVED") {
                    handleClickOpenCreateDialog();
                } else {
                    setSeverityMessage("Order Cannot be Edited Because has Aleardy been Confirmed")
                    setSeverity("error")
                    handleClickAlert()
                }
            },
        },
        {
            id: "delete",
            label: "Delete Order",
            icon: (
                <SvgIcon fontSize="small" sx={{ color: "text.primary" }}>
                    <TrashIcon />
                </SvgIcon>
            ),
            onClick: () => {
                handleClickOpenDeleteDialog();
                setSeverityMessage("Not Allowed to Perform this Action")
                setSeverity("error")
                handleClickAlert()
            },
        },
    ];

    return (
        <>
            {openAlert && (
                <CustomAlert
                    openAlert={openAlert}
                    handleCloseAlert={handleCloseAlert}
                    severity={severity}
                    severityMessage={severityMessage}
                />
            )}
            {popOver.open && (
                <FilterOrderPopper
                    id={popOver.id}
                    anchorEl={popOver.anchorRef}
                    open={popOver.open}
                    onClose={popOver.handleClose}
                    filterItemsData={filterItemsData}
                    setFilterItemsData={setFilterItemsData}
                />
            )}
            {openCreateDialog && (
                <FormDialog
                    open={openCreateDialog}
                    handleClose={handleCloseCreateDialog}
                    dialogTitle={"Order"}
                    action={action}
                    fields={action === CREATE ?
                        formFields.filter(item => item.name !== "product_vendor_id" &&
                            item.name !== "shipping_cost" &&
                            item.name !== "return_amount") :
                        formFields
                    }
                    values={values}
                    url={
                        action === CREATE ? createExternalOrderUrl : updateExternalOrderUrl
                    }
                />
            )}
            {openDataFeedUploadOrdersDialog && (
                <DataFeedUploadDialog
                    open={openDataFeedUploadOrdersDialog}
                    handleClose={handleCloseDataFeedUloadOrdersDialog}
                    ordersData={ordersData}
                    setOrdersData={setOrdersData}
                />
            )}
            {openDeleteDialog && (
                <ConfirmationDialog
                    open={openDeleteDialog}
                    handleClose={handleCloseDeleteDialog}
                    handleAction={handleDelete}
                    isPerformingAction={isDeleting}
                    dialogTitle={"Delete Alert"}
                    dialogContentText={"Are you sure you want to delete this item?"}
                />
            )}
            {openViewDialog &&
                <VendorViewOrder
                    open={openViewDialog}
                    handleClose={handleCloseViewDialog}
                    data={selectedData}
                    setData={setSelectedData}
                />
            }
            <Box sx={{ width: '100%', maxWidth: { sm: '100%', md: '1700px' } }}>
                <Stack spacing={2}>
                    <PageHeader
                        title="Orders"
                        actions={[
                            {
                                label: "Add Order",
                                icon: <SvgIcon fontSize="small"><PlusIcon /></SvgIcon>,
                                onClick: handleClickOpenCreateDialog,
                            },
                            {
                                label: "Download Template",
                                icon: <SvgIcon fontSize="small"><FolderArrowDownIcon /></SvgIcon>,
                                href: "/documents/smai_orders_template.csv",
                                download: true,
                            },
                            {
                                label: "Upload Excel",
                                icon: <SvgIcon fontSize="small"><DocumentDuplicateIcon /></SvgIcon>,
                                fileInput: {
                                    accept: ".csv, .tsv, .xlsx, .xml",
                                    onChange: (e) => {
                                        e.preventDefault();
                                        if (e.target.files?.[0]) {
                                            processFile(e.target.files[0]);
                                            handleClickOpenDataFeedUploadOrdersDialog();
                                        }
                                    },
                                },
                            },
                        ]}
                    />
                    <Box
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            flexWrap: { xs: "wrap", md: "nowrap" },
                        }}
                    >
                        <Box sx={{ mr: "auto", width: { xs: "100%", md: "auto" } }}>
                            <CustomTabs
                                currentTab={currentTab}
                                setCurrentTab={setCurrentTab}
                                tabsData={vendorOrderStatus}
                                style={customTabsStyle}
                                indicatorColor={""}
                            />
                        </Box>
                        <Button
                            onClick={(event) => {
                                popOver.handleOpen(event);
                            }}
                            endIcon={
                                <SvgIcon fontSize="small">
                                    <ChevronDownIcon />
                                </SvgIcon>
                            }
                            variant="contained"
                            sx={{
                                color: "neutral.100",
                                m: 1,
                            }}
                        >
                            Filter By
                        </Button>
                    </Box>
                    <CustomSearch
                        popoverItems={filterItems}
                        handleSearch={handleSearch}
                        exportExcel={exportExcel}
                        getDataForExportExcel={getDataForExportExcel}
                        from={dateFrom}
                        to={dateTo}
                        handleBodyChange={handleDateChange}
                    />
                    <CustomTable
                        order={order}
                        orderBy={orderBy}
                        onRequestSort={handleRequestSort}
                        count={productsOrders.total_results}
                        items={productsOrders.results}
                        onPageChange={handlePageChange}
                        onRowsPerPageChange={handleRowsPerPageChange}
                        onSelectOne={productsOrdersSelection.handleSelectOne}
                        onSelect={onSelect}
                        page={
                            productsOrders.page >= 1
                                ? productsOrders.page - 1
                                : productsOrders.page
                        }
                        rowsPerPage={rowsPerPage}
                        selected={productsOrdersSelection.selected}
                        headCells={externalVendorOrdersHeadCells}
                        popoverItems={orderPopoverItems}
                        isLoading={isLoading}
                    />
                </Stack>
            </Box>
        </>
    );
}

export default VendorOrders;
